import { useApolloClient, useMutation, useQuery, useSubscription } from '@apollo/client/react';

import type {
  ArchiveAllNotificationsMutation,
  ArchiveNotificationMutation,
  ArchiveNotificationMutationVariables,
  InAppNotificationsQuery,
  InAppNotificationsQueryVariables,
  MarkAllNotificationsAsReadMutation,
  MarkNotificationAsReadMutation,
  MarkNotificationAsReadMutationVariables,
  MarkNotificationAsUnreadMutation,
  MarkNotificationAsUnreadMutationVariables,
  NotificationTabCountsQuery,
  NotificationType,
  UnarchiveNotificationMutation,
  UnarchiveNotificationMutationVariables,
  UnreadNotificationCountQuery,
} from '@repo/frontend-shared';
import {
  ArchiveAllNotificationsDocument,
  ArchiveNotificationDocument,
  InAppNotificationsDocument,
  MarkAllNotificationsAsReadDocument,
  MarkNotificationAsReadDocument,
  MarkNotificationAsUnreadDocument,
  NotificationCreatedDocument,
  NotificationReadDocument,
  NotificationTabCountsDocument,
  UnarchiveNotificationDocument,
  UnreadNotificationCountDocument,
} from '@repo/frontend-shared';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';

export type NotificationItem = NonNullable<InAppNotificationsQuery['inAppNotifications']['edges'][number]>['node'];

export interface NotificationFilters {
  type?: NotificationType;
  isRead?: boolean;
  isArchived?: boolean;
}

async function runNotificationAction(action: () => Promise<void>, errorMessage: string): Promise<void> {
  try {
    await action();
  } catch (error) {
    console.error(errorMessage, error);
  }
}

function evictInAppNotificationsCache(client: ReturnType<typeof useApolloClient>): void {
  client.cache.evict({ fieldName: 'inAppNotifications' });
  client.cache.gc();
}

function useInAppNotificationMutations() {
  const [markAsRead] = useMutation<MarkNotificationAsReadMutation, MarkNotificationAsReadMutationVariables>(
    MarkNotificationAsReadDocument,
  );
  const [markAsUnread] = useMutation<MarkNotificationAsUnreadMutation, MarkNotificationAsUnreadMutationVariables>(
    MarkNotificationAsUnreadDocument,
  );
  const [archiveNotification] = useMutation<ArchiveNotificationMutation, ArchiveNotificationMutationVariables>(
    ArchiveNotificationDocument,
  );
  const [unarchiveNotification] = useMutation<UnarchiveNotificationMutation, UnarchiveNotificationMutationVariables>(
    UnarchiveNotificationDocument,
  );
  const [markAllAsRead] = useMutation<MarkAllNotificationsAsReadMutation>(MarkAllNotificationsAsReadDocument);
  const [archiveAll] = useMutation<ArchiveAllNotificationsMutation>(ArchiveAllNotificationsDocument);

  return {
    markAsRead,
    markAsUnread,
    archiveNotification,
    unarchiveNotification,
    markAllAsRead,
    archiveAll,
  };
}

/**
 * Shared in-app notification list + mutation handlers for admin, partner, and web headers.
 */
export function useInAppNotifications(filters?: NotificationFilters) {
  const hasFilters = Boolean(filters && Object.keys(filters).length > 0);
  const { currentUser } = useCurrentUser();
  const client = useApolloClient();
  const mutations = useInAppNotificationMutations();

  const {
    data: notificationsData,
    loading,
    refetch,
    fetchMore,
  } = useQuery<InAppNotificationsQuery, InAppNotificationsQueryVariables>(InAppNotificationsDocument, {
    variables: {
      input: {
        first: 20,
        ...(hasFilters && filters),
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !currentUser?.id,
  });

  const { data: unreadCountData, refetch: refetchUnreadCount } = useQuery<UnreadNotificationCountQuery>(
    UnreadNotificationCountDocument,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 30000,
      skip: !currentUser?.id,
    },
  );

  const { data: tabCountsData, refetch: refetchTabCounts } = useQuery<NotificationTabCountsQuery>(
    NotificationTabCountsDocument,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 30000,
      skip: !currentUser?.id,
    },
  );

  const edges = notificationsData?.inAppNotifications?.edges;
  const notifications: NotificationItem[] = edges ? edges.map(edge => edge.node) : [];

  const refetchAll = async () => {
    await Promise.all([refetch(), refetchUnreadCount(), refetchTabCounts()]);
  };

  useSubscription(NotificationCreatedDocument, {
    skip: !currentUser?.id,
    onData: () => {
      void refetchAll();
    },
  });

  useSubscription(NotificationReadDocument, {
    skip: !currentUser?.id,
    onData: () => {
      void refetchAll();
    },
  });

  const handleMarkAsRead = (notificationId: string) =>
    runNotificationAction(async () => {
      await mutations.markAsRead({ variables: { notificationId } });
      await refetchAll();
    }, 'Failed to mark notification as read:');

  const handleMarkAsUnread = (notificationId: string) =>
    runNotificationAction(async () => {
      await mutations.markAsUnread({ variables: { notificationId } });
      await refetchAll();
    }, 'Failed to mark notification as unread:');

  const handleArchive = (notificationId: string) =>
    runNotificationAction(async () => {
      await mutations.archiveNotification({ variables: { notificationId } });
      evictInAppNotificationsCache(client);
      await refetchAll();
    }, 'Failed to archive notification:');

  const handleUnarchive = (notificationId: string) =>
    runNotificationAction(async () => {
      await mutations.unarchiveNotification({ variables: { notificationId } });
      evictInAppNotificationsCache(client);
      await refetchAll();
    }, 'Failed to unarchive notification:');

  const handleMarkAllAsRead = () =>
    runNotificationAction(async () => {
      await mutations.markAllAsRead();
      await refetchAll();
    }, 'Failed to mark all notifications as read:');

  const handleArchiveAll = () =>
    runNotificationAction(async () => {
      await mutations.archiveAll();
      evictInAppNotificationsCache(client);
      await refetchAll();
    }, 'Failed to archive all notifications:');

  const handleLoadMore = () =>
    runNotificationAction(async () => {
      if (!notificationsData?.inAppNotifications?.pageInfo?.hasNextPage) {
        return;
      }

      await fetchMore({
        variables: {
          input: {
            after: notificationsData.inAppNotifications.pageInfo.endCursor,
            first: 20,
            ...(hasFilters && filters),
          },
        },
      });
    }, 'Failed to load more notifications:');

  return {
    notifications,
    loading,
    unreadCount: unreadCountData?.unreadNotificationCount ?? 0,
    tabCounts: tabCountsData?.notificationTabCounts ?? { all: 0, alerts: 0, scenarios: 0 },
    hasMore: notificationsData?.inAppNotifications?.pageInfo?.hasNextPage ?? false,
    markAsRead: handleMarkAsRead,
    markAsUnread: handleMarkAsUnread,
    archive: handleArchive,
    unarchive: handleUnarchive,
    markAllAsRead: handleMarkAllAsRead,
    archiveAll: handleArchiveAll,
    loadMore: handleLoadMore,
    refetch,
  };
}
